import { IHttpService } from "../../core/service/HttpService";
import GetAllIndexTypeGroupsForCategoryInteractor from "../../indexTypes/interactors/GetAllIndexTypeGroupsForCategory";
import GetAllIndexTypesInteractor from "../../indexTypes/interactors/GetAllIndexTypesInteractor";
import { IIndexType } from "../../indexTypes/model/IndexType";
import { IIndexTypeGroup } from "../../indexTypes/model/IndexTypeGroup";
import { IOrganisationIndex } from "../model/OrganisationIndex";
import { IOrganisationIndexGroup } from "../model/OrganisationIndexGroup";
import CreateOrganisationIndexGroupInteractor from "./CreateOrganisationIndexGroupInteractor";
import CreateOrganisationIndexInteractor from "./CreateOrganisationIndexInteractor";
import GetIndexTypeGroupTreeInteractor from "./GetIndexTypeGroupTreeInteractor";

export interface TResultItem {
  id?: number;
  index?: IOrganisationIndex;
  group?: IOrganisationIndexGroup;
  referenceId: string;
  name: string;
  defaultName: string;
  type: string;
  children: TResultItem[];
  find(name: string): TResultItem | undefined;
  findByReferenceId(id: string): TResultItem | undefined;
  print(prefix: string): string;
}

const normalize = (input?: string) => {
  return input?.replace(/[\r\n\u0085\u2028\u2029]+/g, "") || "";
};

const ResultItem = (
  item: any,
  itemName: string,
  itemType: string,
  defaultName: string,
  itemReferenceId: string
): TResultItem => {
  let _item: any = item;
  let _name: string = itemName,
    _type: string = itemType,
    _defaultName: string = defaultName;
  let _children: TResultItem[] = [];
  let _id: number = -1;
  let _referenceId: string = itemReferenceId;

  let obj = {
    get name() {
      return _name;
    },
    set name(value) {
      _name = value;
    },
    get defaultName() {
      return _defaultName;
    },
    set defaultName(value) {
      _defaultName = value;
    },
    get type() {
      return _type;
    },
    set type(value) {
      _type = value;
    },
    get children() {
      return _children;
    },
    set children(value) {
      _children = value;
    },

    get id() {
      return _id;
    },
    set id(value) {
      _id = value;
    },
    get referenceId() {
      return _referenceId;
    },
    set referenceId(value) {
      _referenceId = value;
    },
    find(name: string): TResultItem | undefined {
      let itemHasName = Object.keys(_item).some((key) => _item[key] === name);
      if (obj.name === name || obj.defaultName === name || itemHasName) {
        return obj;
      }
      for (let itm of _children) {
        let result = itm.find(name);
        if (result) {
          return result;
        }
      }
      return undefined;
    },
    findByReferenceId(id: string): TResultItem | undefined {
      if (obj.referenceId === id) {
        return obj;
      }
      for (let itm of _children) {
        let result = itm.findByReferenceId(id);
        if (result) {
          return result;
        }
      }
      return undefined;
    },
    print(prefix: string): string {
      let result = "";
      result += `${prefix}${_type} - ${_name}\n`;
      for (let itm of _children) {
        result += itm.print(prefix + "  ");
      }
      return result;
    }
  };
  return obj;
};

export type TKeyConfig = {
  DEFAULT_NAME_KEY: string;
  ORG_KEY: string;
  TYPE_KEY: string;
  PARENT_KEY: string;
};
export default class ImportOrganisationDescriptorsInteractor {
  private getIndexTypeGroupTree: GetIndexTypeGroupTreeInteractor;
  private createOrganisationIndexGroup: CreateOrganisationIndexGroupInteractor;
  private getAllIndexTypeGroupsForCategory: GetAllIndexTypeGroupsForCategoryInteractor;
  private getAllIndexTypes: GetAllIndexTypesInteractor;
  private createOrganisationIndex: CreateOrganisationIndexInteractor;
  private httpService: IHttpService;
  constructor({
    getIndexTypeGroupTree,
    createOrganisationIndexGroup,
    getAllIndexTypeGroupsForCategory,
    getAllIndexTypes,
    createOrganisationIndex,
    httpService
  }: any) {
    this.getIndexTypeGroupTree = getIndexTypeGroupTree;
    this.createOrganisationIndexGroup = createOrganisationIndexGroup;
    this.getAllIndexTypeGroupsForCategory = getAllIndexTypeGroupsForCategory;
    this.getAllIndexTypes = getAllIndexTypes;
    this.createOrganisationIndex = createOrganisationIndex;
    this.httpService = httpService;
  }

  collectItems(
    url: string,
    items: any[],
    keyConfig: {
      DEFAULT_NAME_KEY: string;
      ORG_KEY: string;
      TYPE_KEY: string;
      PARENT_KEY: string;
    }
  ) {
    const { PARENT_KEY, DEFAULT_NAME_KEY, ORG_KEY, TYPE_KEY } = keyConfig;
    let resultItems: TResultItem[] = [];
    items.forEach((item, index) => {
      let obj: TResultItem;
      if (item[TYPE_KEY]) {
        let name = normalize(item[ORG_KEY]);
        if (name.length > 0) {
          if (item[PARENT_KEY] == null) {
            obj = ResultItem(
              item,
              name,
              normalize(item[TYPE_KEY]),
              normalize(item[DEFAULT_NAME_KEY]),
              url + index.toString()
            );
            resultItems.push(obj);
          } else {
            obj = ResultItem(
              item,
              name,
              normalize(item[TYPE_KEY]),
              normalize(item[DEFAULT_NAME_KEY]),
              url + index.toString()
            );
            for (let itm of resultItems) {
              itm.find(normalize(item[PARENT_KEY]))?.children.push(obj);
            }
          }
        }
      }
    });
    return resultItems;
  }

  async createGroup(
    organisationId: number,
    languageId: number,
    categoryId: number,
    groupTypes: IIndexTypeGroup[],
    indexTypes: IIndexType[],
    item: TResultItem,
    parentId: number
  ) {
    if (item.type.toLowerCase() === "deskriptor") {
      let indexId = await this.createIndex(
        indexTypes,
        item,
        organisationId,
        languageId,
        {
          id: parentId
        }
      );
      return indexId;
    }
    let group = await this.createOrganisationIndexGroup.execute({
      name: item.name,
      nameTranslations: [
        {
          organisationId: organisationId,
          languageId,
          nameTranslation: item.name,
          valid: true
        }
      ],
      organisationIndexCategoryId: categoryId,
      organisationId: organisationId,
      indexTypeGroupId:
        groupTypes.find((g) => g.name.toLowerCase() === item.type.toLowerCase())
          ?.id || -1,
      parentId: parentId,
      valid: true
    } as any);
    item.id = group.id;
    item.group = group;
    for (const child of item.children) {
      if (child.type.toLowerCase() === "institut") {
        let groupId = await this.createGroup(
          organisationId,
          languageId,
          categoryId,
          groupTypes,
          indexTypes,
          child,
          group.id
        );
        child.id = groupId;
      } else {
        await this.createIndex(
          indexTypes,
          child,
          organisationId,
          languageId,
          group
        );
      }
    }
    return group.id;
  }

  private async createIndex(
    indexTypes: IIndexType[],
    child: TResultItem,
    organisationId: number,
    languageId: number,
    group: any
  ) {
    let index = await this.createOrganisationIndex.execute({
      indexTypeId:
        indexTypes.find(
          (type) => type.name.toLowerCase() === child.type.toLowerCase()
        )?.id || -1,
      name: child.name,
      nameTranslations: [
        {
          organisationId: organisationId,
          languageId,
          nameTranslation: child.name,
          valid: true
        }
      ],
      organisationGroupId: group.id,
      organisationId,
      valid: true
    } as any);
    child.id = index.id;
    child.index = index;
    return index.id;
  }

  async execute(params: {
    url: string;
    importOptions: {
      sheet: string;
      keys: TKeyConfig;
    };
    organisationId: number;
    categoryId: number;
    indexGroupId: number;
    languageId: number;
    dryRun?: boolean;
    debug?: boolean;
  }) {
    const {
      url,
      importOptions,
      organisationId,
      categoryId,
      indexGroupId,
      languageId = 2,
      debug = false,
      dryRun = true
    } = params;
    debug && console.table(params);
    let lawItems: TResultItem[] = [];
    const tree = await this.getIndexTypeGroupTree.execute(
      organisationId,
      categoryId
    );
    const categoryTypeId = tree?.[0]?.indexTypeCategoryId;
    const groupTypes = await this.getAllIndexTypeGroupsForCategory.execute(
      categoryTypeId
    );
    const indexTypes = await this.getAllIndexTypes.execute();

    const indexDataResponse = await this.httpService.get(url);
    const indexData = await indexDataResponse.json();

    lawItems = this.collectItems(
      url,
      indexData[importOptions.sheet],
      importOptions.keys
    );

    if (!dryRun) {
      for (const item of lawItems) {
        let groupId = await this.createGroup(
          organisationId,
          languageId,
          categoryId,
          groupTypes,
          indexTypes,
          item,
          indexGroupId
        );
        item.id = groupId;
      }
    }

    if (debug) {
      console.log([lawItems.map((item) => item.print("")).join("")]);
    }
    return lawItems;
  }
}

export { ResultItem };
